import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import ProtectedRoute, { PublicRoute } from './components/ProtectedRoute';
import Layout from './components/Layout/Layout';
import ErrorSnackbar from './components/ErrorSnackbar';
import Login from './pages/Login';
import SetPassword from './pages/SetPassword';
import Overview from './pages/Overview';
import Files from './pages/Files';
import Logs from './pages/Logs';
import AuditLogs from './pages/AuditLogs';
import BackupDestinations from './pages/BackupDestinations';
import Schedule from './pages/Schedule';
import Restore from './pages/Restore';
import Password from './pages/Password';
import Profile from './pages/Profile';
import Users from './pages/Users';
import Groups from './pages/Groups';
import RolePermissions from './pages/RolePermissions';
import FileWatchers from './pages/FileWatchers';
import StoragePools from './pages/StoragePools';
import { STORAGE_POOLS_UI } from './config/features';
import AlertSettings from './pages/AlertSettings';

// Create Material-UI theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        {/* Global Error Snackbar */}
        <ErrorSnackbar />

        <Routes>
          {/* Public routes */}
          <Route
            path="/login"
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            }
          />
          {/* QA #75: the 設定密碼 page the invite / reset mails link to (?token=…) */}
          <Route
            path="/set-password"
            element={
              <PublicRoute>
                <SetPassword />
              </PublicRoute>
            }
          />

          {/* Protected routes - wrapped with Layout */}
          <Route
            path="/overview"
            element={
              <ProtectedRoute>
                <Layout>
                  <Overview />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/files"
            element={
              <ProtectedRoute>
                <Layout>
                  <Files />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/logs"
            element={
              <ProtectedRoute>
                <Layout>
                  <Logs />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/audit-logs"
            element={
              <ProtectedRoute>
                <Layout>
                  <AuditLogs />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/backup-destinations"
            element={
              <ProtectedRoute requiredRoles={['admin', 'sub_admin']}>
                <Layout>
                  <BackupDestinations />
                </Layout>
              </ProtectedRoute>
            }
          />
          {/* Redirect old cloud-settings to new backup-destinations */}
          <Route path="/cloud-settings" element={<Navigate to="/backup-destinations" replace />} />
          <Route
            path="/schedule"
            element={
              <ProtectedRoute requiredRoles={['admin', 'sub_admin']}>
                <Layout>
                  <Schedule />
                </Layout>
              </ProtectedRoute>
            }
          />
          {/* QA #60: admin / sub_admin only (mirrors backend /settings gate) */}
          <Route
            path="/restore"
            element={
              <ProtectedRoute requiredRoles={['admin', 'sub_admin']}>
                <Layout>
                  <Restore />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/password"
            element={
              <ProtectedRoute>
                <Layout>
                  <Password />
                </Layout>
              </ProtectedRoute>
            }
          />
          {/* QA #2 / D1: self-service profile (e-mail); all roles */}
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Layout>
                  <Profile />
                </Layout>
              </ProtectedRoute>
            }
          />
          {/* ADR-050: admin / sub_admin (mirrors backend /storage-pools gate); off → falls
              through to the catch-all (config/features.ts). */}
          {STORAGE_POOLS_UI && (
            <Route
              path="/storage-pools"
              element={
                <ProtectedRoute requiredRoles={['admin', 'sub_admin']}>
                  <Layout>
                    <StoragePools />
                  </Layout>
                </ProtectedRoute>
              }
            />
          )}
          <Route
            path="/file-watchers"
            element={
              <ProtectedRoute requiredRoles={['admin', 'sub_admin']}>
                <Layout>
                  <FileWatchers />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/users"
            element={
              <ProtectedRoute requiredRoles={['admin', 'sub_admin']}>
                <Layout>
                  <Users />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/groups"
            element={
              <ProtectedRoute requiredRoles={['admin']}>
                <Layout>
                  <Groups />
                </Layout>
              </ProtectedRoute>
            }
          />
          {/* QA #84: read-only role permission table, admin only */}
          <Route
            path="/role-permissions"
            element={
              <ProtectedRoute requiredRoles={['admin']}>
                <Layout>
                  <RolePermissions />
                </Layout>
              </ProtectedRoute>
            }
          />
          {/* QA #60: admin / sub_admin only (mirrors backend /alerts gate) */}
          <Route
            path="/alert-settings"
            element={
              <ProtectedRoute requiredRoles={['admin', 'sub_admin']}>
                <Layout>
                  <AlertSettings />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Dashboard alias - redirect to overview */}
          <Route path="/dashboard" element={<Navigate to="/overview" replace />} />

          {/* Redirect root to overview */}
          <Route path="/" element={<Navigate to="/overview" replace />} />

          {/* 404 - redirect to overview */}
          <Route path="*" element={<Navigate to="/overview" replace />} />
        </Routes>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
